import { toggleProductCardImage } from '../ProductCard';

export const listingEventsMethods = {
    onRootClick(event) {
        const filtersOpenTrigger = event.target.closest(
            '[data-listing-filters-open]'
        );

        if (filtersOpenTrigger) {
            event.preventDefault();
            this.syncPendingFiltersFromState();
            this.filtersSidebar?.open();

            if (!this.filterCountsLoaded) {
                this.loadFilterTermCounts();
            }

            this.schedulePendingFiltersCountPreview();
            return;
        }

        const filtersApplyTrigger = event.target.closest(
            '[data-listing-filters-apply]'
        );

        if (filtersApplyTrigger) {
            event.preventDefault();
            this.state.filters = this.cloneFilters(this.pendingFilters);
            this.state.page = 1;
            this.filtersSidebar?.close();
            this.fetchAndRender({ pushUrl: this.getListingUrl() });
            return;
        }

        const filtersClearTrigger = event.target.closest(
            '[data-listing-filters-clear]'
        );

        if (filtersClearTrigger) {
            event.preventDefault();
            event.stopPropagation();

            const isSidebarClear = Boolean(
                this.filtersSidebar?.content?.contains(filtersClearTrigger)
            );

            if (isSidebarClear) {
                this.clearSidebarFilters({ applyToListing: true });
                return;
            }

            this.clearAppliedListingFilters();
            return;
        }

        const activeFilterRemoveTrigger = event.target.closest(
            '[data-listing-active-filter-remove]'
        );

        if (activeFilterRemoveTrigger) {
            event.preventDefault();

            const filterKey = activeFilterRemoveTrigger.dataset.filterKey || '';
            const filterValue =
                activeFilterRemoveTrigger.dataset.filterValue || '';

            if (!filterKey || !filterValue) {
                return;
            }

            const nextFilters = this.cloneFilters(this.state.filters);
            const currentValue = nextFilters[filterKey];

            if (Array.isArray(currentValue)) {
                const nextValues = currentValue.filter(
                    value => value !== filterValue
                );

                if (nextValues.length > 0) {
                    nextFilters[filterKey] = nextValues;
                } else {
                    delete nextFilters[filterKey];
                }
            } else if (currentValue === filterValue) {
                delete nextFilters[filterKey];
            }

            this.state.filters = nextFilters;
            this.state.page = 1;
            this.syncPendingFiltersFromState();
            this.fetchAndRender({ pushUrl: this.getListingUrl() });
            return;
        }

        const filterToggle = event.target.closest(
            '[data-listing-filter-toggle]'
        );

        if (filterToggle) {
            event.preventDefault();

            const node = filterToggle.closest(
                '[data-listing-filter-group], [data-listing-filter-node]'
            );
            const content = node?.querySelector(
                ':scope > [data-listing-filter-children]'
            );
            const icon = filterToggle.querySelector('i');
            const isExpanded =
                filterToggle.getAttribute('aria-expanded') === 'true';

            if (content) {
                content.classList.toggle('hidden', isExpanded);
            }

            filterToggle.setAttribute(
                'aria-expanded',
                isExpanded ? 'false' : 'true'
            );
            icon?.classList.toggle('rotate-180', !isExpanded);
            return;
        }

        const filterSelect = event.target.closest(
            '[data-listing-filter-select]'
        );

        if (filterSelect) {
            event.preventDefault();

            const taxonomy =
                filterSelect.dataset.taxonomy || this.state.taxonomy;
            const slug = filterSelect.dataset.termSlug || '';
            const node = filterSelect.closest('[data-listing-filter-node]');
            if (!taxonomy || !slug || !node) {
                return;
            }

            const selectedSlugs = new Set(this.pendingFilters[taxonomy] || []);
            const isSelected = this.isSlugCoveredByPendingSelection(
                taxonomy,
                slug
            );
            const usesBranchSelection =
                this.usesHierarchicalFilterSelection(taxonomy);
            const nodeSlugs = this.getFilterNodeSlugs(node);
            const descendantsMap =
                this.bootstrap.termDescendants?.[taxonomy] || {};

            if (isSelected) {
                if (usesBranchSelection) {
                    nodeSlugs.forEach(item => selectedSlugs.delete(item));

                    Array.from(selectedSlugs).forEach(selectedSlug => {
                        const branch = descendantsMap[selectedSlug] || [];

                        if (branch.includes(slug)) {
                            selectedSlugs.delete(selectedSlug);
                        }
                    });
                } else {
                    selectedSlugs.delete(slug);
                }
            } else if (usesBranchSelection) {
                nodeSlugs.forEach(item => selectedSlugs.add(item));
            } else {
                selectedSlugs.add(slug);
            }

            this.pendingFilters[taxonomy] = this.sortFilterValuesByDomOrder(
                taxonomy,
                Array.from(selectedSlugs)
            );

            this.updatePendingFilterSelectionUi();
            this.schedulePendingFiltersCountPreview();
            return;
        }

        const sortToggle = event.target.closest('[data-listing-sort-toggle]');

        if (sortToggle) {
            event.preventDefault();
            this.toggleSortMenu();
            return;
        }

        const sortOption = event.target.closest('[data-listing-sort-option]');

        if (sortOption) {
            event.preventDefault();

            const nextSort = sortOption.dataset.sortValue || 'popular';
            const isSidebarSortOption = Boolean(
                this.filtersSidebar?.content?.contains(sortOption)
            );

            if (nextSort !== this.state.sort) {
                this.state.sort = nextSort;
                this.state.page = 1;

                if (!isSidebarSortOption) {
                    this.filtersSidebar?.close();
                }

                this.fetchAndRender({ pushUrl: this.getListingUrl() });
            }

            this.closeSortMenu();
            return;
        }

        const switchImageButton = event.target.closest('[js-switch-image]');

        if (switchImageButton) {
            event.preventDefault();
            event.stopPropagation();
            toggleProductCardImage(switchImageButton);

            return;
        }

        const loadMoreTrigger =
            event.target.closest('[data-listing-load-more] button') ||
            event.target.closest('[data-listing-load-more]');

        if (loadMoreTrigger) {
            event.preventDefault();

            const maxPages = Number(
                this.resultsPagination?.dataset.maxPages || 1
            );
            const currentPage = Number(
                this.resultsPagination?.dataset.page || this.state.page || 1
            );

            if (currentPage >= maxPages) {
                return;
            }

            this.state.page = currentPage + 1;
            this.fetchAndRender({ appendResults: true, showLoader: true });
            return;
        }

        const termsScrollTrigger = event.target.closest(
            '[data-listing-terms-prev], [data-listing-terms-next]'
        );

        if (termsScrollTrigger) {
            event.preventDefault();

            const carousel = termsScrollTrigger.closest(
                '[data-listing-terms-carousel]'
            );

            this.scrollTermsTrack(
                carousel,
                termsScrollTrigger.hasAttribute('data-listing-terms-prev')
                    ? -1
                    : 1
            );
            return;
        }

        const link = event.target.closest(
            '[data-listing-term], [data-listing-term-back]'
        );

        if (!link) {
            return;
        }

        return;
    },

    onDocumentClick(event) {
        if (!this.sortRoot || this.sortRoot.contains(event.target)) {
            return;
        }

        this.closeSortMenu();
    },

    onPopState(event) {
        const nextState = event.state?.listingState;

        if (!nextState) {
            window.location.reload();
            return;
        }

        this.state = { ...nextState };
        this.fetchAndRender({ pushUrl: null });
    },
};
